import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Topbar from "../components/Topbar";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Wallet, Loader2, ReceiptText } from "lucide-react";

const eur = (n) => new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(n || 0);

const MONTHS = ["gennaio", "febbraio", "marzo", "aprile", "maggio", "giugno", "luglio", "agosto", "settembre", "ottobre", "novembre", "dicembre"];
const monthLabel = (ym) => {
  const [y, m] = (ym || "").split("-");
  const idx = parseInt(m, 10) - 1;
  if (isNaN(idx) || !MONTHS[idx]) return ym;
  return `${MONTHS[idx][0].toUpperCase()}${MONTHS[idx].slice(1)} ${y}`;
};
const fmtDate = (s) => { try { return new Date(s).toLocaleDateString("it-IT"); } catch { return s; } };

export default function FormatoreCommissions() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/formatore/commissions")
      .then((r) => setData(r.data))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, []);

  const rate = data ? Math.round(data.rate * 100) : 30;

  return (
    <div data-testid="formatore-commissions" className="min-h-screen bg-brand-bg">
      <Topbar />
      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="mb-6">
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-brand-gold mb-2">
            <Wallet size={14} /> Area Formatore
          </div>
          <h1 className="text-3xl sm:text-4xl text-brand-ink mb-1">Estratto conto</h1>
          <p className="text-sm text-brand-muted">Provvigioni sui tuoi corsi venduti e pagati · aliquota {rate}%</p>
        </div>

        <div className="flex gap-1 mb-6 border-b border-brand-line">
          <Link to="/formatore" className="px-3 py-2 text-sm font-semibold text-brand-muted hover:text-brand-blue">I miei corsi</Link>
          <span className="px-3 py-2 text-sm font-semibold text-brand-blue border-b-2 border-brand-blue -mb-px">Estratto conto</span>
        </div>

        {loading ? (
          <div className="flex items-center gap-2 text-brand-muted py-12 justify-center">
            <Loader2 className="animate-spin" size={18} /> Caricamento…
          </div>
        ) : !data || data.months.length === 0 ? (
          <div className="bg-white border border-brand-line rounded-sm p-10 text-center text-brand-muted text-sm">
            <ReceiptText size={22} className="inline mb-2 opacity-60" />
            <div>Nessuna provvigione ancora maturata.</div>
            <div className="text-xs mt-1">Le provvigioni compaiono quando un ordine con un tuo corso viene pagato.</div>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-8">
              <Metric label="Totale maturato" value={eur(data.total_commission)} accent="green" />
              <Metric label="Da liquidare" value={eur(data.total_unsettled)} accent="gold" />
              <Metric label="Già liquidato" value={eur(data.total_settled)} />
            </div>

            <div className="space-y-6">
              {data.months.map((m) => (
                <div key={m.month} className="bg-white border border-brand-line rounded-sm overflow-hidden">
                  <div className="flex items-center justify-between px-4 py-3 bg-brand-bg border-b border-brand-line">
                    <div className="font-semibold text-brand-ink">{monthLabel(m.month)}</div>
                    <div className="text-sm">
                      <span className="text-brand-muted mr-2">{m.count} vendit{m.count === 1 ? "a" : "e"}</span>
                      <span className="font-semibold text-emerald-700">{eur(m.total_commission)}</span>
                    </div>
                  </div>
                  <table className="w-full text-sm">
                    <thead className="text-brand-muted">
                      <tr className="border-b border-brand-line">
                        <th className="text-left font-semibold px-4 py-2">Corso</th>
                        <th className="text-left font-semibold px-4 py-2">Acquirente</th>
                        <th className="text-left font-semibold px-4 py-2">Data pagamento</th>
                        <th className="text-right font-semibold px-4 py-2">Incassato</th>
                        <th className="text-right font-semibold px-4 py-2">Provvigione</th>
                        <th className="text-right font-semibold px-4 py-2">Stato</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-brand-line">
                      {m.rows.map((r, i) => (
                        <tr key={r.order_id + "-" + r.course_id + "-" + i}>
                          <td className="px-4 py-2 text-brand-ink">{r.course_title || "—"}</td>
                          <td className="px-4 py-2 text-brand-muted">{r.buyer || "—"}</td>
                          <td className="px-4 py-2 text-brand-muted">{fmtDate(r.paid_at)}</td>
                          <td className="px-4 py-2 text-right text-brand-muted">{eur(r.gross_amount)}</td>
                          <td className="px-4 py-2 text-right font-medium text-brand-ink">{eur(r.commission_amount)}</td>
                          <td className="px-4 py-2 text-right">
                            {r.settled ? (
                              <span className="text-xs font-semibold px-2 py-0.5 rounded-sm bg-emerald-50 text-emerald-700">Liquidata</span>
                            ) : (
                              <span className="text-xs font-semibold px-2 py-0.5 rounded-sm bg-amber-50 text-amber-700">Da liquidare</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ))}
            </div>

            <p className="text-xs text-brand-muted mt-6">
              Le provvigioni sono calcolate sull'incassato reale di ogni corso e liquidate pro-soluto (solo su ordini pagati).
            </p>
          </>
        )}
      </main>
    </div>
  );
}

function Metric({ label, value, accent }) {
  const color = accent === "green" ? "text-emerald-700" : accent === "gold" ? "text-brand-gold-deep" : "text-brand-ink";
  return (
    <div className="bg-white border border-brand-line rounded-sm px-4 py-3">
      <div className="text-xs text-brand-muted">{label}</div>
      <div className={`text-2xl font-semibold ${color}`}>{value}</div>
    </div>
  );
}
